import { useEffect, lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";

// 1. Global Components
import { Header } from "@/components/Header";

// Homepage loaded eagerly
import Index from "./pages/Index";

// All other pages lazy-loaded
const NotFound = lazy(() => import("./pages/NotFound"));
const PrivacyArchitecture = lazy(() => import("./pages/PrivacyArchitecture"));
const About = lazy(() => import("./pages/About"));
const Terms = lazy(() => import("./pages/Terms"));
const Refund = lazy(() => import("./pages/Refund"));
const Documentation = lazy(() => import("./pages/Documentation"));
const Pricing = lazy(() => import("./pages/Pricing"));
const Careers = lazy(() => import("./pages/Careers"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Vulnerability = lazy(() => import("./pages/Vulnerability"));
const Contact = lazy(() => import("./pages/Contact"));
const Guidelines = lazy(() => import("./pages/Guidelines"));
const Features = lazy(() => import("./pages/Features"));
const Integrations = lazy(() => import("./pages/Integrations"));
const UnifiedWorkspace = lazy(() => import("./pages/UnifiedWorkspace"));
const AiMessageSummaries = lazy(() => import("./pages/AiMessageSummaries"));
const AiConversationSummaries = lazy(() => import("./pages/AiConversationSummaries"));
const AiMessageDigest = lazy(() => import("./pages/AiMessageDigest"));
const AiThreadSummaries = lazy(() => import("./pages/AiThreadSummaries"));
const AiChatRecaps = lazy(() => import("./pages/AiChatRecaps"));
const AiAutomaticSummaries = lazy(() => import("./pages/AiAutomaticSummaries"));
const AiSmartSummaries = lazy(() => import("./pages/AiSmartSummaries"));
const AiSmartConversation = lazy(() => import("./pages/AiSmartConversation"));
const DailyChatSummary = lazy(() => import("./pages/DailyChatSummary"));
const MeetingConversationSummary = lazy(() => import("./pages/MeetingConversationSummary"));
const SummarizeMessagesAutomatically = lazy(() => import("./pages/SummarizeMessagesAutomatically"));
const SummarizeLongConversations = lazy(() => import("./pages/SummarizeLongConversations"));
const SummarizeGroupChats = lazy(() => import("./pages/SummarizeGroupChats"));
const AiMessageAssistant = lazy(() => import("./pages/AiMessageAssistant"));
const AiChatDigest = lazy(() => import("./pages/AiChatDigest"));
const AiSummaryEngine = lazy(() => import("./pages/AiSummaryEngine"));
const MessagePrioritySummary = lazy(() => import("./pages/MessagePrioritySummary"));
const ConversationInsights = lazy(() => import("./pages/ConversationInsights"));
const SummarizeLongMessages = lazy(() => import("./pages/SummarizeLongMessages"));
const SummarizeUnreadMessages = lazy(() => import("./pages/SummarizeUnreadMessages"));
const CommunicationInsights = lazy(() => import("./pages/CommunicationInsights"));
const AiSummarizesChatsEmails = lazy(() => import("./pages/AiSummarizesChatsEmails"));
const SummarizeMessages = lazy(() => import("./pages/SummarizeMessages"));
const AiMessageDigestEngine = lazy(() => import("./pages/AiMessageDigestEngine"));
const AiResponseEngine = lazy(() => import("./pages/AiResponseEngine"));
const AiToneSettings = lazy(() => import("./pages/AiToneSettings"));
const AiPersonaModeling = lazy(() => import("./pages/AiPersonaModeling"));
const AiIdentityStudio = lazy(() => import("./pages/AiIdentityStudio"));
const AiWritingStyle = lazy(() => import("./pages/AiWritingStyle"));
const AiToneAndAlerts = lazy(() => import("./pages/AiToneAndAlerts"));
const AiPriorityInbox = lazy(() => import("./pages/AiPriorityInbox"));
const AiMessageOrganizer = lazy(() => import("./pages/AiMessageOrganizer"));
const AiConversationInsights = lazy(() => import("./pages/AiConversationInsights"));
const AiCommunicationHub = lazy(() => import("./pages/AiCommunicationHub"));
const AiOmnichannelHub = lazy(() => import("./pages/AiOmnichannelHub"));
const AiUnifiedWorkspace = lazy(() => import("./pages/AiUnifiedWorkspace"));
const AiMeetingAndEmailSummaries = lazy(() => import("./pages/AiMeetingAndEmailSummaries"));
const GmailIntegration = lazy(() => import("./pages/gmail-integration"));
const SlackIntegration = lazy(() => import("./pages/slack-integration"));
const TeamsIntegration = lazy(() => import("./pages/teams-integration"));
const OutlookIntegration = lazy(() => import("./pages/outlook-integration"));
const InstagramIntegration = lazy(() => import("./pages/instagram-integration"));
const FacebookIntegration = lazy(() => import("./pages/facebook-integration"));
const Autoreplier = lazy(() => import("./pages/autoreplier"));
const SmartFaq = lazy(() => import("./pages/smart-faq"));
const CalendarSync = lazy(() => import("./pages/calendar-sync"));
const PriorityHub = lazy(() => import("./pages/priority-hub"));
const Summaries = lazy(() => import("./pages/summaries"));
const Workflows = lazy(() => import("./pages/Workflows"));
const AiCommunicationAssistant = lazy(() => import("./pages/AiCommunicationAssistant"));
const DailyMessageSummary = lazy(() => import("./pages/DailyMessageSummary"));
const ImportantConversationSummaries = lazy(() => import("./pages/ImportantConversationSummaries"));
const ImportantEmailSummaries = lazy(() => import("./pages/ImportantEmailSummaries"));
const AiEmailOrganizer = lazy(() => import("./pages/AiEmailOrganizer"));
const LeadResponseAutomation = lazy(() => import("./pages/LeadResponseAutomation"));
const CustomerConversationTracker = lazy(() => import("./pages/CustomerConversationTracker"));
const CentralizedClientMessaging = lazy(() => import("./pages/CentralizedClientMessaging"));
const queryClient = new QueryClient();
const FilterInternalChatter = lazy(() => import("./pages/FilterInternalChatter"));
const BestAiEmailTools = lazy(() => import("./pages/BestAiEmailTools"));
const UnifiedInboxSmallBusiness = lazy(() => import("./pages/UnifiedInboxSmallBusiness"));
const AllMessagingPlatforms = lazy(() => import("./pages/AllMessagingPlatforms"));
const EcommerceSharedInbox = lazy(() => import("./pages/EcommerceSharedInbox"));
const UnifiedMessagingDashboard = lazy(() => import("./pages/UnifiedMessagingDashboard"));
const AiMeetingSchedulerTeamsSlack = lazy(() => import("./pages/AiMeetingSchedulerTeamsSlack"));
const MetaBusinessSuiteAlternativeIg = lazy(() => import("./pages/MetaBusinessSuiteAlternativeIg"));
const AiSlackCatchup = lazy(() => import("./pages/AiSlackCatchup"));
const InstagramDmAutomation = lazy(() => import("./pages/InstagramDmAutomation"));
const UnifiedWorkCommunication = lazy(() => import("./pages/UnifiedWorkCommunication"));
const OutlookPriorityAssistant = lazy(() => import("./pages/OutlookPriorityAssistant"));
const SharedInboxCollision = lazy(() => import("./pages/SharedInboxCollision"));
const SuperhumanAlternativeForTeams = lazy(() => import("./pages/SuperhumanAlternativeForTeams"));
const ZapierAlternativeMessaging = lazy(() => import("./pages/ZapierAlternativeMessaging"));
const MetaBusinessSuiteAlternative = lazy(() => import("./pages/MetaBusinessSuiteAlternative"));
const ClearMakesVsCompetitors = lazy(() => import("./pages/ClearMakesVsCompetitors"));
const WhyClearMakes = lazy(() => import("./pages/WhyClearMakes"));
const AppAggregatorAlternative = lazy(() => import("./pages/AppAggregatorAlternative"));
const RR = lazy(() => import("./pages/rr"));

const ClearMakesVsCalendly = lazy(() => import("./pages/clearmakes-vs-calendly"));
const ClearMakesVsIntercom = lazy(() => import("./pages/clearmakes-vs-intercom"));
const ClearMakesVsHelpScout = lazy(() => import("./pages/clearmakes-vs-help-scout"));
const ClearMakesVsMonday = lazy(() => import("./pages/clearmakes-vs-monday"));
const ClearMakesVsNotionAI = lazy(() => import("./pages/clearmakes-vs-notion-ai"));
const ClearMakesVsClickUp = lazy(() => import("./pages/clearmakes-vs-clickup"));
const ClearMakesVsFront = lazy(() => import("./pages/clearmakes-vs-front"));
const ClearMakesVsSuperhuman = lazy(() => import("./pages/clearmakes-vs-superhuman"));
const ClearMakesVsShortwave = lazy(() => import("./pages/clearmakes-vs-shortwave"));
const ClearMakesVsSanebox = lazy(() => import("./pages/clearmakes-vs-sanebox"));
const ClearMakesVsMetaBusinessSuite = lazy(() => import("./pages/clearmakes-vs-meta-business-suite"));
const ClearMakesVsBeeper = lazy(() => import("./pages/clearmakes-vs-beeper"));
const ClearMakesVsZendesk = lazy(() => import("./pages/ClearMakesVsZendesk"));
const ClearMakesVsGmailGemini = lazy(() => import("./pages/ClearMakesVsGmailGemini"));
const ClearMakesVsOutlookCopilot = lazy(() => import("./pages/ClearMakesVsOutlookCopilot"));
const ClearMakesVsSparkMail = lazy(() => import("./pages/ClearMakesVsSparkMail"));
const ClearMakesVsRambox = lazy(() => import("./pages/ClearMakesVsRambox"));
const ClearMakesVsSlackThreads = lazy(() => import("./pages/ClearMakesVsSlackThreads"));
const Solutions = lazy(() => import("./pages/Solutions"));
const ForFounders = lazy(() => import("./pages/ForFounders"));
const ForFreelancers = lazy(() => import("./pages/ForFreelancers"));
const ForRemoteTeams = lazy(() => import("./pages/ForRemoteTeams"));
const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

const CanonicalUpdater = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    const canonical = `https://clearmakes.com${pathname === "/" ? "/" : pathname.replace(/\/$/, "")}`;
    let link = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!link) {
      link = document.createElement("link");
      link.setAttribute("rel", "canonical");
      document.head.appendChild(link);
    }
    link.setAttribute("href", canonical);
  }, [pathname]);
  return null;
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      
      <Sonner />
      <BrowserRouter>
        <ScrollToTop />
        <CanonicalUpdater />
        <Header />
        
        <Suspense fallback={null}>
          <Routes>
            {/* Core Routes */}
            <Route path="/" element={<Index />} />
            <Route path="/homepage" element={<Navigate to="/" replace />} />
            
            <Route path="/homepage.html" element={<Navigate to="/" replace />} />
            <Route path="/about" element={<About />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/refund" element={<Refund />} />
            <Route path="/documentation" element={<Documentation />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/features" element={<Features />} />
            <Route path="/integrations" element={<Integrations />} />
            <Route path="/careers" element={<Careers />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/vulnerability" element={<Vulnerability />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/guidelines" element={<Guidelines />} />
            <Route path="/security-architecture" element={<PrivacyArchitecture />} />

            
            <Route path="/rr" element={<RR />} />
            
            {/* AI & Summary Features */}
            <Route path="/ai-communication-assistant" element={<AiCommunicationAssistant />} />
            <Route path="/daily-message-summary" element={<DailyMessageSummary />} />
            <Route path="/important-conversation-summaries" element={<ImportantConversationSummaries />} />
            <Route path="/important-email-summaries" element={<ImportantEmailSummaries />} />
            <Route path="/ai-smart-conversation" element={<AiSmartConversation />} />
            <Route path="/unifiedworkspace" element={<UnifiedWorkspace />} />
            <Route path="/aimessagesummaries" element={<AiMessageSummaries />} />
            <Route path="/aiconversationsummaries" element={<AiConversationSummaries />} />
            <Route path="/ai-message-digest" element={<AiMessageDigest />} />
            <Route path="/ai-thread-summaries" element={<AiThreadSummaries />} />
            <Route path="/ai-chat-recaps" element={<AiChatRecaps />} />
            <Route path="/ai-automatic-summaries" element={<AiAutomaticSummaries />} />
            <Route path="/ai-smart-summaries" element={<AiSmartSummaries />} />
            <Route path="/dailychatsummary" element={<DailyChatSummary />} />
            <Route path="/meeting-conversation-summary" element={<MeetingConversationSummary />} />
            <Route path="/summarize-messages-automatically" element={<SummarizeMessagesAutomatically />} />
            <Route path="/summarize-long-conversations" element={<SummarizeLongConversations />} />
            <Route path="/summarize-group-chats" element={<SummarizeGroupChats />} />
            <Route path="/ai-chat-digest" element={<AiChatDigest />} />
            <Route path="/ai-message-assistant" element={<AiMessageAssistant />} />
            <Route path="/message-priority-summary" element={<MessagePrioritySummary />} />
            <Route path="/conversation-insights" element={<ConversationInsights />} />
            <Route path="/communication-insights" element={<CommunicationInsights />} />
            <Route path="/summarize-long-messages" element={<SummarizeLongMessages />} />
            <Route path="/summarize-unread-messages" element={<SummarizeUnreadMessages />} />
            <Route path="/ai-summarizes-chats-emails" element={<AiSummarizesChatsEmails />} />
            <Route path="/summarize-messages" element={<SummarizeMessages />} />
            <Route path="/ai-summary-engine" element={<AiSummaryEngine />} />
            <Route path="/ai-message-digest-engine" element={<AiMessageDigestEngine />} />
            <Route path="/ai-response-engine" element={<AiResponseEngine />} />
            <Route path="/ai-tone-settings" element={<AiToneSettings />} />
            <Route path="/ai-persona-modeling" element={<AiPersonaModeling />} />
            <Route path="/ai-identity-studio" element={<AiIdentityStudio />} />
            <Route path="/ai-writing-style" element={<AiWritingStyle />} />
            <Route path="/ai-tone-and-alerts" element={<AiToneAndAlerts />} />
            <Route path="/ai-priority-inbox" element={<AiPriorityInbox />} />
            <Route path="/ai-message-organizer" element={<AiMessageOrganizer />} />
            <Route path="/ai-conversation-insights" element={<AiConversationInsights />} />
            <Route path="/ai-communication-hub" element={<AiCommunicationHub />} />
            <Route path="/ai-omnichannel-hub" element={<AiOmnichannelHub />} />
            <Route path="/ai-meeting-and-email-summaries" element={<AiMeetingAndEmailSummaries />} />

            {/* Integration Routes */}
            <Route path="/gmail-integration" element={<GmailIntegration />} />
            <Route path="/slack-integration" element={<SlackIntegration />} />
            <Route path="/teams-integration" element={<TeamsIntegration />} />
            <Route path="/outlook-integration" element={<OutlookIntegration />} />
            <Route path="/instagram-integration" element={<InstagramIntegration />} />
            <Route path="/facebook-integration" element={<FacebookIntegration />} />
            <Route path="/autoreplier" element={<Autoreplier />} />
            <Route path="/smart-faq" element={<SmartFaq />} />
            <Route path="/calendar-sync" element={<CalendarSync />} />
            <Route path="/priority-hub" element={<PriorityHub />} />
            <Route path="/summaries" element={<Summaries />} />
            <Route path="/workflows" element={<Workflows />} />
            <Route path="/ai-conversation-insights" element={<AiConversationInsights />} />
            <Route path="/ai-email-organizer" element={<AiEmailOrganizer />} />
            <Route path="/lead-response-automation" element={<LeadResponseAutomation />} />
            <Route path="/customer-conversation-tracker" element={<CustomerConversationTracker />} />
            <Route path="/centralized-client-messaging" element={<CentralizedClientMessaging />} />
            <Route path="/customer-conversation-tracker" element={<CustomerConversationTracker />} />
            <Route path="/best-ai-email-tools" element={<BestAiEmailTools />} />
            <Route path="/filter-internal-chatter" element={<FilterInternalChatter />} />
            <Route path="/unified-inbox-small-business" element={<UnifiedInboxSmallBusiness />} />
            <Route path="/unified-messaging-dashboard" element={<UnifiedMessagingDashboard />} />
            <Route path="/all-messaging-platforms" element={<AllMessagingPlatforms />} />
            <Route path="/ecommerce-shared-inbox" element={<EcommerceSharedInbox />} />
            <Route path="/ai-meeting-scheduler-teams-slack" element={<AiMeetingSchedulerTeamsSlack />} />
            <Route path="/meta-business-suite-alternative-ig" element={<MetaBusinessSuiteAlternativeIg />} />
            <Route path="/ai-slack-catchup" element={<AiSlackCatchup />} />
            <Route path="/instagram-dm-automation" element={<InstagramDmAutomation />} />
            <Route path="/unified-work-communication" element={<UnifiedWorkCommunication />} />
            <Route path="/outlook-priority-assistant" element={<OutlookPriorityAssistant />} />
            <Route path="/shared-inbox-collision" element={<SharedInboxCollision />} />
            <Route path="/superhuman-alternative-for-teams" element={<SuperhumanAlternativeForTeams />} />
            <Route path="/zapier-alternative-messaging" element={<ZapierAlternativeMessaging />} />
            <Route path="/meta-business-suite-alternative" element={<MetaBusinessSuiteAlternative />} />
            <Route path="/clearmakes-vs-competitors" element={<ClearMakesVsCompetitors />} />
            <Route path="/why-clearmakes" element={<WhyClearMakes />} />
            <Route path="/app-aggregator-alternative" element={<AppAggregatorAlternative />} />
            <Route path="/clearmakes-vs-calendly" element={<ClearMakesVsCalendly />} />
<Route path="/clearmakes-vs-intercom" element={<ClearMakesVsIntercom />} />
<Route path="/clearmakes-vs-help-scout" element={<ClearMakesVsHelpScout />} />
<Route path="/clearmakes-vs-monday" element={<ClearMakesVsMonday />} />
<Route path="/clearmakes-vs-notion-ai" element={<ClearMakesVsNotionAI />} />
<Route path="/clearmakes-vs-clickup" element={<ClearMakesVsClickUp />} />
<Route path="/clearmakes-vs-zendesk" element={<ClearMakesVsZendesk />} />
<Route path="/clearmakes-vs-gmail-gemini" element={<ClearMakesVsGmailGemini />} />
<Route path="/clearmakes-vs-outlook-copilot" element={<ClearMakesVsOutlookCopilot />} />
<Route path="/clearmakes-vs-spark-mail" element={<ClearMakesVsSparkMail />} />
<Route path="/clearmakes-vs-rambox" element={<ClearMakesVsRambox />} />
<Route path="/clearmakes-vs-slack-threads" element={<ClearMakesVsSlackThreads />} />


<Route path="/clearmakes-vs-front" element={<ClearMakesVsFront />} />
<Route path="/clearmakes-vs-superhuman" element={<ClearMakesVsSuperhuman />} />
<Route path="/clearmakes-vs-shortwave" element={<ClearMakesVsShortwave />} />
<Route path="/clearmakes-vs-sanebox" element={<ClearMakesVsSanebox />} />
<Route path="/clearmakes-vs-meta-business-suite" element={<ClearMakesVsMetaBusinessSuite />} />
<Route path="/clearmakes-vs-beeper" element={<ClearMakesVsBeeper />} />
<Route path="/solutions" element={<Solutions />} />
<Route path="/for-founders" element={<ForFounders />} />
<Route path="/for-freelancers" element={<ForFreelancers />} />
<Route path="/for-remote-teams" element={<ForRemoteTeams />} />





            {/* Catch-all 404 */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;